import React from 'react';
import {
  Bone,
  Shield,
  Info,
  ChevronRight,
  AlertCircle,
  Clock,
  CheckCircle2,
  Heart
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const SaludDentalCard = ({ saludDental }) => {
  if (!saludDental) return null;

  return (
    <Card className="overflow-hidden bg-white">
      {/* Encabezado del Card */}
      <CardHeader className="border-b bg-gradient-to-r from-pink-500/10 to-transparent p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg shadow-sm">
            <Bone className="h-5 w-5 text-white" />
          </div>
          <div>
            <CardTitle className="text-xl font-semibold text-gray-800">
              Salud Dental
            </CardTitle>
            <p className="text-base text-gray-500 mt-1">
              Evaluación y cuidados bucales
            </p>
          </div>
        </div>
      </CardHeader>

      {/* Contenido principal */}
      <CardContent className="p-4 space-y-4">
        {/* Estado General */}
        <div className="bg-white rounded-lg border border-gray-100 shadow-sm">
          <div className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-pink-500" />
                  <h3 className="text-lg font-medium text-gray-900">Estado Dental</h3>
                </div>
                <div className="flex items-start gap-2 bg-pink-50/40 p-3 rounded-lg">
                  <Info className="h-5 w-5 text-pink-500 mt-0.5 flex-shrink-0" />
                  <p className="text-base text-gray-700">{saludDental.estado}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-pink-100/80 text-pink-700">
                  <Clock className="h-4 w-4 mr-1" />
                  Revisión Anual
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Cuidados Preventivos */}
        {saludDental.cuidadosPreventivos && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Rutina de Cuidado */}
            <div className="bg-gradient-to-br from-pink-50 to-white rounded-lg border border-pink-100 hover:border-pink-200 transition-colors">
              <div className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Heart className="h-5 w-5 text-pink-500" />
                  <h4 className="text-lg font-medium text-gray-900">Rutina de Cuidado</h4>
                </div>

                <div className="space-y-2 mt-1">
                  {saludDental.cuidadosPreventivos.rutina && (
                    <div className="flex items-start gap-2 bg-white/60 p-3 rounded-lg group hover:bg-white/80 transition-all">
                      <CheckCircle2 className="h-5 w-5 mt-0.5 text-pink-500 flex-shrink-0 group-hover:scale-110 transition-transform" />
                      <div>
                        <p className="text-base font-medium text-pink-700 mb-1">Rutina Diaria</p>
                        <p className="text-base text-gray-600">
                          {saludDental.cuidadosPreventivos.rutina}
                        </p>
                      </div>
                    </div>
                  )}

                  {saludDental.cuidadosPreventivos.herramientas && (
                    <div className="flex items-start gap-2 bg-white/60 p-3 rounded-lg group hover:bg-white/80 transition-all">
                      <CheckCircle2 className="h-5 w-5 mt-0.5 text-pink-500 flex-shrink-0 group-hover:scale-110 transition-transform" />
                      <div>
                        <p className="text-base font-medium text-pink-700 mb-1">Herramientas</p>
                        <p className="text-base text-gray-600">
                          {saludDental.cuidadosPreventivos.herramientas}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Señales de Alerta */}
            <div className="bg-gradient-to-br from-orange-50 to-white rounded-lg border border-orange-100">
              <div className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-500" />
                  <h4 className="text-lg font-medium text-gray-900">Señales de Alerta</h4>
                </div>

                {saludDental.cuidadosPreventivos.senalesAlerta && (
                  <div className="space-y-2 mt-1">
                    {saludDental.cuidadosPreventivos.senalesAlerta
                      .split('. ')
                      .map((senal, idx) =>
                        senal ? (
                          <div
                            key={idx}
                            className="flex items-start gap-2 bg-white/60 p-3 rounded-lg group hover:bg-white/80 transition-all border border-transparent hover:border-orange-100"
                          >
                            <ChevronRight className="h-5 w-5 mt-0.5 text-orange-500 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
                            <p className="text-base text-gray-600">{senal}.</p>
                          </div>
                        ) : null
                      )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Recomendaciones Adicionales */}
        <div className="bg-blue-50/50 rounded-lg border border-blue-100 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Info className="h-5 w-5 text-blue-500" />
            <h4 className="text-lg font-medium text-gray-900">Recordatorio Importante</h4>
          </div>
          <p className="text-base text-blue-700 bg-white/60 p-3 rounded-lg">
            La revisión dental regular es clave para prevenir problemas de salud.
            Programa chequeos periódicos con tu veterinario.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default SaludDentalCard;
